import React, { memo, useCallback, useMemo, useState } from "react";

import { DragDropContext } from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";

import { useLibraryItems } from "./hooks/useLibraryItems";
import { useMultiDnDWorker } from "./hooks/useMultiDnDWorker";
import { useSelectedItems } from "./hooks/useSelectedItems";
import { SelectBoxContext } from "./SelectBoxContext";
import SelectBoxPanel from "./SelectBoxPanel";
import { moveToRank, renumberRanks, sortByRank } from "./selectBoxRanks";
import "../../css/admin/admin.scss";
import "../../css/admin/utils/select-box.scss";
import "../../css/common/checkbox.scss";

import type { DraggableLocation, DropResult } from "@hello-pangea/dnd";
import type { JSX } from "react";

import type { SelectBoxFilterKey } from "./hooks/useLibraryItems";
import type {
  SelectBoxDisplayItem,
  SelectBoxSeedItem,
  SelectBoxTab,
} from "./selectBox.types";
import type { SelectBoxType } from "./types/selectBoxTypes";
import type { MultiDnDResult } from "./worker/multiDnDWorker";

interface SelectBoxProps {
  filters?: SelectBoxFilterKey[];
  hasRank?: boolean;
  ignoreSmartlistsAudios?: boolean;
  items: SelectBoxSeedItem[];
  label: string;
  onChangeItems: (items: SelectBoxDisplayItem[]) => void;
  tabs: SelectBoxTab[];
  selectBoxType?: SelectBoxType;
}

function SelectBox({
  filters,
  hasRank,
  ignoreSmartlistsAudios,
  items,
  label,
  onChangeItems,
  tabs,
  selectBoxType,
}: SelectBoxProps): JSX.Element {
  const { t } = useTranslation("editor-utils");
  const hasCastingList = tabs.some((tab) => tab.key === "castings");

  const hasMediaList = tabs.some(
    (tab) => tab.key === "contents" || tab.key === "audios",
  );

  const hasSmartlistList = tabs.some(
    (tab) => tab.key === "standard" || tab.key === "series",
  );

  const hasDisplayList = tabs.some((tab) => tab.key === "displays");

  const {
    selectedItems,
    updateSelectedItems,
    selectedStatus,
    setSelectedStatus,
    selectedSearch,
    setSelectedSearch,
    selectedFilteredItems,
  } = useSelectedItems({
    items,
    hasRank,
    onNormalizeRanks: onChangeItems,
    hasMediaList,
    hasCastingList,
    hasSmartlistList,
    hasDisplayList,
  });

  const [multiSelectList, setMultiSelectList] = useState<
    SelectBoxDisplayItem[]
  >([]);
  const [multiOrderingParams, setMultiOrderingParams] = useState<{
    src: string;
    dst: string;
  } | null>(null);

  const {
    libraryItems,
    libraryStatus,
    setLibraryStatus,
    librarySearch,
    setLibrarySearch,
    libraryFilteredItems,
    selectedTab,
    setSelectedTab,
  } = useLibraryItems({
    selectedItems,
    filters,
    multiSelectList,
    ignoreSmartlistsAudios,
    selectBoxType,
    hasMediaList,
    hasCastingList,
    hasSmartlistList,
    hasDisplayList,
  });

  // Rows keep their place while a rank is edited (the input stays under the cursor), sorted once the edit ends.
  const changeSelectedItemRank = useCallback(
    (uuid: string, newRank: number): void => {
      const newItemList = moveToRank(selectedItems, uuid, newRank);

      if (newItemList === selectedItems) return;

      updateSelectedItems(newItemList);
      onChangeItems(sortByRank(newItemList));
    },
    [selectedItems, updateSelectedItems, onChangeItems],
  );

  const endRankEdit = useCallback((): void => {
    const sortedItemList = sortByRank(selectedItems);
    const isSorted = sortedItemList.every(
      (sortedItem, index) => sortedItem === selectedItems[index],
    );

    if (!isSorted) updateSelectedItems(sortedItemList);
  }, [selectedItems, updateSelectedItems]);

  const context = useMemo(
    () => ({
      selectedItems,
      updateSelectedItems,
      selectedStatus,
      setSelectedStatus,
      selectedSearch,
      setSelectedSearch,
      selectedFilteredItems,
      libraryItems,
      libraryStatus,
      setLibraryStatus,
      librarySearch,
      setLibrarySearch,
      libraryFilteredItems,
      selectedTab,
      setSelectedTab,
      multiSelectList,
      setMultiSelectList,
      computeOrdering: multiOrderingParams !== null,
      changeSelectedItemRank,
      endRankEdit,
    }),
    [
      selectedItems,
      updateSelectedItems,
      selectedStatus,
      setSelectedStatus,
      selectedSearch,
      setSelectedSearch,
      selectedFilteredItems,
      libraryItems,
      libraryStatus,
      setLibraryStatus,
      librarySearch,
      setLibrarySearch,
      libraryFilteredItems,
      selectedTab,
      setSelectedTab,
      multiSelectList,
      multiOrderingParams,
      changeSelectedItemRank,
      endRankEdit,
    ],
  );

  const getContextParams = (src: string, dst: string) => {
    const isDroppableSelected = src === "droppableSelected";

    const sourceItems = isDroppableSelected
      ? context.selectedItems
      : context.libraryItems;
    const sourceFilteredItems = isDroppableSelected
      ? context.selectedFilteredItems
      : context.libraryFilteredItems;
    const destinationItems =
      dst === "droppableSelected"
        ? context.selectedItems
        : context.libraryItems;

    const search = isDroppableSelected
      ? context.selectedSearch
      : context.librarySearch;
    const filter = isDroppableSelected
      ? context.selectedStatus
      : context.libraryStatus;

    return {
      search,
      filter,
      sourceItems,
      destinationItems,
      sourceFilteredItems,
    };
  };

  const updateSelectedItemList = (itemList: SelectBoxDisplayItem[]): void => {
    const newItemList = hasRank ? renumberRanks(itemList) : itemList;

    context.updateSelectedItems(newItemList);
    onChangeItems(newItemList);
  };

  const updateSelectedLibraryItems = (
    src: string,
    sourceItems: SelectBoxDisplayItem[],
    dst: string,
    destinationItems: SelectBoxDisplayItem[],
  ): void => {
    if (src === "droppableSelected") {
      updateSelectedItemList(sourceItems);
    }

    // Within the selection, source and destination are the same list.
    if (dst === "droppableSelected" && src !== dst) {
      updateSelectedItemList(destinationItems);
    }
  };

  const reorder = (
    source: DraggableLocation,
    destination: DraggableLocation,
  ): void => {
    const {
      search,
      filter,
      sourceItems,
      destinationItems,
      sourceFilteredItems,
    } = getContextParams(source.droppableId, destination.droppableId);

    let { index } = source;
    let item = sourceItems[index];

    if (search || filter !== "all") {
      const filteredItem = sourceFilteredItems[source.index];
      if (!filteredItem) return;

      item = filteredItem;
      const foundIndex = sourceItems.findIndex(
        (content) => content.uuid === filteredItem.uuid,
      );
      index = foundIndex !== -1 ? foundIndex : index;
    }

    if (!item) return;

    sourceItems.splice(index, 1);
    destinationItems.splice(destination.index, 0, item);

    updateSelectedLibraryItems(
      source.droppableId,
      sourceItems,
      destination.droppableId,
      destinationItems,
    );
  };

  const onDragEnd = (result: DropResult): void => {
    if (result?.source && result?.destination) {
      const { source, destination } = result;
      reorder(source, destination);
    }
  };

  const handleMultiDnDMessage = (
    message: MessageEvent<MultiDnDResult>,
  ): void => {
    if (!message) return;

    const { sourceItems, src, destinationItems, dst } = message.data;

    // Make sure we reset isChecked state for selected items
    destinationItems.forEach((item) => {
      // eslint-disable-next-line no-param-reassign
      item.isChecked = false;
    });

    updateSelectedLibraryItems(src, sourceItems, dst, destinationItems);

    setMultiSelectList([]);

    // Reset ordering params, this will remove spinner animation
    setMultiOrderingParams(null);
  };

  // If the worker fails, don't leave the spinner stuck forever
  const handleMultiDnDError = (): void => {
    setMultiSelectList([]);
    setMultiOrderingParams(null);
  };

  const { isWorkerAvailable, postMultiDnDMessage } = useMultiDnDWorker({
    onMessage: handleMultiDnDMessage,
    onError: handleMultiDnDError,
  });

  const multiDnD = (src: string, dst: string): void => {
    if (multiSelectList.length === 0) return;
    if (!isWorkerAvailable()) return;

    setMultiOrderingParams({ src, dst });

    const contextParams = getContextParams(src, dst);
    const isFiltered = contextParams.search || contextParams.filter !== "all";

    try {
      // This is heavy computation, let's use web worker to not freeze UI
      postMultiDnDMessage({
        msg: "onMultiDnD",
        src,
        dst,
        contextParams: {
          ...contextParams,
          // Skip cloning sourceFilteredItems into the worker when it won't be read (no search/filter active).
          sourceFilteredItems: isFiltered
            ? contextParams.sourceFilteredItems
            : undefined,
        },
        multiSelectList,
        appendToDestination: hasRank && dst === "droppableSelected",
      });
    } catch {
      setMultiSelectList([]);
      setMultiOrderingParams(null);
    }
  };

  const forwardMultiDnD = (): void => {
    multiDnD("droppableLibrary", "droppableSelected");
  };

  const backMultiDnD = (): void => {
    multiDnD("droppableSelected", "droppableLibrary");
  };

  return (
    <div className="editor-selectbox-container">
      <SelectBoxContext.Provider value={context}>
        <DragDropContext onDragEnd={onDragEnd}>
          <SelectBoxPanel
            key="select-box-panel-libraries"
            tabs={tabs}
            title={t("elementsLibrary")}
          />
          <div className="selectbox-panel select-box-panel-btn">
            <i className="material-icons" onClick={forwardMultiDnD}>
              arrow_forward
            </i>
            <i className="material-icons" onClick={backMultiDnD}>
              arrow_back
            </i>
          </div>
          <SelectBoxPanel
            key="select-box-panel-selected"
            selected
            hasRank={hasRank}
            title={label}
          />
        </DragDropContext>
      </SelectBoxContext.Provider>
    </div>
  );
}

export default memo(SelectBox);
